import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'

import avatarPlaceHolder from 'assets/images/user_placeholder.png'
import { IconChatBUbblesOutline } from 'assets/svg/sprite'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { someTimeAgoMessages } from 'lib/dayjs'
import { setPollShowChat } from 'services/endpoints/message'

import styles from './_poll_answers.module.scss'

type PollUser = {
  id: string
  name?: string
  username: string
  display_name?: string
  avatar?: { id: number; url?: string; created_at?: string } | []
  conversation_id?: string | null
  isSubscribed?: boolean | null
}
type PollAnswerItem = {
  id: string
  text?: string
  created_at: string
  user: PollUser
}

type PollEntry = {
  id: string
  question: string
  created_at: string
  answers: PollAnswerItem[]
  show_chat?: boolean
}

export const PollAnswers = ({ pollAnswers }: { pollAnswers: PollEntry[] }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  // local copy ONLY so we can disable button after update (optional)
  const [polls, setPolls] = useState<PollEntry[]>(pollAnswers ?? [])

  const [openPollId, setOpenPollId] = useState<string | null>(null)

  const [visibleCounts, setVisibleCounts] = useState<Record<string, number>>({})

  const [saving, setSaving] = useState<Record<string, boolean>>({})

  useEffect(() => {
    setPolls(pollAnswers ?? [])
  }, [pollAnswers])

  useEffect(() => {
    if (!polls?.length) return
    setVisibleCounts(prev => {
      const next = { ...prev }
      let changed = false
      for (const p of polls) {
        if (next[p.id] == null) {
          next[p.id] = 5
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [polls])

  const pollDate = (pollDate: string) => {
    const date = new Date(pollDate)
    const h = ('0' + date.getHours()).slice(-2)
    const m = ('0' + date.getMinutes()).slice(-2)
    return `${h}:${m}`
  }

  if (!polls || polls.length === 0) return null

  const toggleDropdown = (pollId: string) => {
    setOpenPollId(prev => (prev === pollId ? null : pollId))
  }

  const showMore = (pollId: string) => {
    setVisibleCounts(prev => ({
      ...prev,
      [pollId]: (prev[pollId] ?? 5) + 5
    }))
  }

  // sets show_chat=false in DB
  const onDelete = async (pollId: string) => {
    setSaving(prev => ({ ...prev, [pollId]: true }))
    try {
      await setPollShowChat(pollId, false)
      queryClient.invalidateQueries(['conversations'])

    } catch (e) {
      console.error(e)
    } finally {
      setSaving(prev => ({ ...prev, [pollId]: false }))
    }
  }

  return (
    <>
      {polls.map(poll => {
        const answers = poll?.answers ?? []
        if (answers.length === 0) return null

        const isOpen = openPollId === poll.id
        const visible = visibleCounts[poll.id] ?? 5
        const visibleAnswers = answers.slice(0, visible)
        const hasMore = answers.length > visible

        const isSaving = !!saving[poll.id]
        const isAlreadyHidden = poll.show_chat === false // only if API returns show_chat

        return (
          <ActionCard
            key={poll.id}
            isDropdownOpen={isOpen}
            text={
              <div
                className={styles.question_container}
                onClick={() => toggleDropdown(poll.id)}
              >
                <div className={styles.question_icons}>
                  <IconChatBUbblesOutline color="#ffffff" width="30" height="30" />
                </div>

                <div className={styles.question_text_container}>
                  <div className={styles.responses}>
                    {answers.length} {answers.length > 1 ? t('Responses') : t('Response')}
                  </div>
                  <div className={styles.question_text}>{poll.question}</div>
                </div>

                <div className={styles.question_time_container}>
                  <div className={styles.question_time}>
                    {someTimeAgoMessages(new Date(`${poll.created_at}`))}
                  </div>
                  <div className={styles.new_respones}>{answers.length}</div>
                </div>
              </div>
            }
            dropDownContent={
              <div className={styles.answers_container}>
                <div className={styles.answers_list}>
                  {visibleAnswers.map(answer => (
                    <div
                      className={styles.answer}
                      key={answer.id}
                      onClick={() => {
                        const user = answer.user

                        const convId =
                          typeof user.conversation_id === 'string' &&
                          user.conversation_id.length > 0
                            ? user.conversation_id
                            : null

                        navigate(`/chat/${user.id}`, {
                          state: {
                            otherUserData: {
                              ...user,
                              isSubscribed: Boolean(user.isSubscribed)
                            },
                            ...(convId
                              ? { conversationId: convId }
                              : {})
                          }
                        })
                      }}
                    >
                      <div className={styles.answer_avatar}>
                        <img
                          src={
                            (answer.user.avatar as any)?.url
                              ? (answer.user.avatar as any).url
                              : avatarPlaceHolder
                          }
                          alt="user avatar"
                        />
                      </div>

                      <div className={styles.answer_text_container}>
                        <div className={styles.answer_name_container}>
                          <div className={styles.answer_name}>
                            {answer.user.display_name ??
                              answer.user.name ??
                              answer.user.username}
                          </div>
                          <div className={styles.answer_username}>
                            @{answer.user.username.toLowerCase()}
                          </div>
                          <div className={styles.answer_time}>
                            {pollDate(answer.created_at)}
                          </div>
                        </div>
                        <div className={styles.answer_text}>{answer.text}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className={styles.show_more_container}>
                    <button
                      type="button"
                      className={styles.show_more_btn}
                      onClick={e => {
                        e.stopPropagation()
                        showMore(poll.id)
                      }}
                    >
                      {t('Show older...')}
                    </button>
                  </div>
                )}

                {/* Hide group row (NEW LINE, FAR RIGHT) */}
                <div className={styles.hide_group_row}>
                  <button
                    type="button"
                    className={styles.hide_group_btn}
                    disabled={isSaving || isAlreadyHidden}
                    onClick={e => {
                      e.stopPropagation()
                      onDelete(poll.id)
                    }}
                  >
                    {t('Hide Group')}
                  </button>
                </div>
              </div>
            }
          />
        )
      })}
    </>
  )
}
